/**
 * Files, as somebody looking for a document sees them.
 *
 * One flat list with a search box, because that is how the looking actually
 * goes: you know roughly what the file is called, not which cohort it was
 * hung off. The cohort is shown against each file anyway, so a candidate can
 * tell "the March intake's checklist" from "the June one" at a glance —
 * those two documents differ, and opening the wrong one wastes a trip to an
 * embassy.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  api, ApiError, fileDownloadUrl, fileSize, type SharedFile,
} from '../api';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function Files() {
  const [files, setFiles] = useState<SharedFile[]>([]);
  const [everything, setEverything] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<{ files: SharedFile[]; seeingEverything: boolean }>('/files');
        setFiles(res.files);
        setEverything(res.seeingEverything);
      } catch (e) { setError((e as ApiError).message); }
      finally { setLoading(false); }
    })();
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return files;
    return files.filter((f) =>
      f.name.toLowerCase().includes(needle)
      || (f.description ?? '').toLowerCase().includes(needle)
      || (f.cohortName ?? '').toLowerCase().includes(needle));
  }, [files, q]);

  if (loading) return <><h1>Files</h1><p className="muted">Loading…</p></>;

  return (
    <>
      <h1>Files</h1>

      {error && <p className="auth__error">{error}</p>}

      {everything && (
        <p className="notice notice--warn">
          You are an administrator, so this is every file in the portal — not what a trainee sees.
          Each one says who it reaches.
        </p>
      )}

      {files.length === 0 ? (
        <p className="muted">
          Nothing has been shared with you yet. Documents that apply to everyone, and to your
          intake, will appear here.
        </p>
      ) : (
        <>
          <div className="filters">
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search files" aria-label="Search files" size={28}
            />
            <span className="muted small">
              {shown.length === files.length
                ? `${files.length} file${files.length === 1 ? '' : 's'}`
                : `${shown.length} of ${files.length}`}
            </span>
          </div>

          <ul className="filelist">
            {shown.map((f) => (
              <li key={f.id} className="filelist__item">
                <div className="filelist__body">
                  <a className="filelist__name" href={fileDownloadUrl(f.mediaFileId)}>{f.name}</a>
                  {f.description && <p className="muted small">{f.description}</p>}
                  <p className="muted small">
                    {f.cohortName
                      ? <span className="pill pill--soft">{f.cohortName}</span>
                      : <span className="pill pill--soft">Everyone</span>}
                    {' '}
                    {fileSize(f.sizeBytes)}{f.sizeBytes != null ? ' · ' : ''}shared {day(f.sharedAt)}
                  </p>
                </div>
                <a className="btn" href={fileDownloadUrl(f.mediaFileId)}>Download</a>
              </li>
            ))}
            {shown.length === 0 && <li className="muted">Nothing matches “{q}”.</li>}
          </ul>
        </>
      )}
    </>
  );
}
